import { memo, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ShieldCheck, AlertTriangle, Flag, Ban, FileMinus, Layers, ImageOff, FileQuestion, Lock, CheckCircle2, Search, X, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RebuildAll } from "../components/kit/RebuildAll";
import { http, errMsg } from "../lib/api";
import { StatusBadge, kitStatus } from "../components/StatusBadge";
import { CheckAllPanel } from "../components/kit/CheckAllPanel";
import { PublishBlockers } from "../components/kit/PublishBlockers";
import { DeliveryStatus } from "../components/ops/DeliveryStatus";
import { Autopilot } from "../components/ops/Autopilot";
import { StyleGuidePicker } from "../components/kit/StyleGuidePicker";
import { FillDays } from "../components/kit/FillDays";
import { StdInfo } from "../components/kit/StdInfo";
import { PriceStrip } from "../components/kit/PriceStrip";
import { LookPanel } from "../components/kit/LookPanel";
import { LightFill } from "../components/kit/LightFill";
import { Fold } from "../components/Fold";
import { CostByTier } from "../components/ops/CostByTier";
import { OtherLookPanel } from "../components/kit/OtherLookPanel";
import { JobStopBadge } from "../components/kit/JobStops";
import { RepairsPanel } from "../components/ops/RepairsPanel";
import { EditionGroup, groupByEdition } from "../components/kit/EditionGroup";
import { LiveToggle } from "../components/kit/LiveToggle";
import { JobActions } from "../components/kit/JobActions";
import { KitFilters, applyFilter } from "../components/kit/KitFilters";
import { fmtSecs } from "../components/kit/SpeedPanel";

const price = (k) => Number(k.product?.price) || 0;
const BUSY = ["generating", "verifying", "queued", "fact_checking", "fixing"];
const isBusy = (k) => BUSY.includes(k.status) || k.filling;
const ago = (iso, now) => Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
const sub = (k) => (k.topic_kit ? `${k.season} · ${k.topic_kit.label} kit` : ["standalone", "guide"].includes(k.collection) ? ((k.subjects || []).join(" · ") || "PDF only") : `${k.season} · ${k.days}d`);
const aud = (n) => `A$${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 2 })}`;
const PAY = {
  no_quote: ["No quote yet", "bg-stone-100 text-stone-600"],
  deposit_due: [(b) => `Deposit due ${aud(b.first)}`, "bg-amber-50 text-amber-800 border border-amber-200"],
  payment_due: [(b) => `Payment due ${aud(b.first)}`, "bg-amber-50 text-amber-800 border border-amber-200"],
  deposit_paid: [(b) => `Deposit paid · ${aud(b.balance)} balance due`, "bg-sky-50 text-sky-800 border border-sky-200"],
  paid: ["Paid in full", "bg-emerald-50 text-emerald-800 border border-emerald-200"],
};
const fmtD = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "");

const RemindBtn = ({ k, reload }) => {
  const b = k.bespoke;
  const [busy, setBusy] = useState(false);
  if (!b.can_remind) return null;
  const send = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await http.post(`/bespoke/requests/${b.request_id}/balance-reminder`); toast.success(`Balance reminder emailed to ${b.name}`); reload(); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <button data-testid={`bespoke-remind-${k.id}`} onClick={send} disabled={busy || !b.has_email} title={b.has_email ? `Email ${b.name} about the ${aud(b.balance)} balance` : "Add the customer's email in Bespoke Requests first"}
        className="inline-flex items-center gap-1 rounded-sm border border-[#8A5A44]/40 bg-white px-1.5 py-0.5 text-[#8A5A44] hover:bg-[#8A5A44] hover:text-white transition-colors disabled:opacity-40">
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Remind
      </button>
      {b.reminded_at && <span data-testid={`bespoke-reminded-${k.id}`} className="text-stone-500">sent {new Date(b.reminded_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>}
    </span>
  );
};

const BespokeInfo = ({ k, reload }) => {
  const b = k.bespoke;
  const [label, cls] = PAY[b.pay] || [];
  return (
    <div data-testid={`bespoke-info-${k.id}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[11px]">
      <span data-testid={`bespoke-customer-${k.id}`} className="font-semibold text-[#8A5A44]">{b.name}</span>
      {b.start_date && <span data-testid={`bespoke-dates-${k.id}`} className="text-stone-600">{fmtD(b.start_date)} – {fmtD(b.end_date)}</span>}
      {label && <span data-testid={`bespoke-pay-${k.id}`} className={`rounded-sm px-1.5 py-0.5 ${cls}`}>{typeof label === "function" ? label(b) : label}</span>}
      <RemindBtn k={k} reload={reload} />
    </div>
  );
};

const Stat = ({ label, value, id }) => (
  <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid={id}>
    <div className="eyebrow">{label}</div>
    <div className="font-display text-4xl mt-2 text-[#2B2A26]">{value}</div>
  </div>
);

const Step = ({ k }) => {
  const pct = k.progress?.total ? Math.min(99, Math.round((k.progress.done / k.progress.total) * 100)) : null;
  return (<>
    <div data-testid={`kit-phase-${k.id}`} className="text-sm font-semibold text-[#2B2A26] truncate">
      {k.phase || (k.filling ? "Filling pages" : k.status)}{k.restarts ? <span className="ml-2 text-[11px] font-normal text-stone-500">restarted {k.restarts}×</span> : null}
    </div>
    <div className="text-xs text-stone-500 truncate" title={k.progress?.current}>{k.progress?.current}</div>
    {pct != null && <div className="mt-1 h-1 bg-stone-100 rounded-sm overflow-hidden"><div className="h-full bg-[#3B82F6] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>}
  </>);
};

const JobInfo = ({ k, now }) => {
  const secs = k.step_at ? ago(k.step_at, now) : null;
  const slow = secs != null && secs >= 600;
  const stale = !k.heartbeat_at || ago(k.heartbeat_at, now) > 150;
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <span data-testid={`kit-elapsed-${k.id}`} className={`font-mono-ui ${slow ? "text-red-700 font-bold" : "text-stone-700"}`}>{secs == null ? "—" : fmtSecs(secs)}{slow ? " · slow" : ""}</span>
      {stale ? <span data-testid={`kit-stale-${k.id}`} className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={11} /> no heartbeat</span> : <span className="text-emerald-700">running</span>}
    </div>
  );
};

const Pill = ({ id, icon: Icon, n, tip, tone }) => (
  <span data-testid={id} title={tip} className={`inline-flex items-center gap-1 ${tone}`}><Icon size={12} />{n}</span>
);

const issues = (k) => {
  const pc = k.page_check || {};
  return [
    ["flagged", Flag, k.counts.flagged, `${k.counts.flagged} venue(s) flagged — review on Verification`, "text-amber-600"],
    ["failed", Ban, k.counts.failed, `${k.counts.failed} venue(s) failed web check — replace or remove`, "text-red-600"],
    ["light", FileMinus, pc.fresh ? pc.light : 0, `${pc.light} light page(s) under 85% full`, "text-amber-600"],
    ["spill", Layers, pc.fresh ? (pc.overflow ?? pc.fails) : 0, `${pc.overflow ?? pc.fails} page(s) spilling onto the next page`, "text-red-600"],
    ["images", ImageOff, k.missing_images, `${k.missing_images} chapter image(s) missing in the interactive app`, "text-stone-500"],
    ["unchecked", FileQuestion, k.sections && !pc.fresh ? "" : 0, "Pages not checked since the last edit", "text-stone-400"],
    ["blockers", Lock, k.blockers, `${k.blockers} item(s) blocking publish`, "text-red-600"],
  ].filter(([, , n]) => n !== 0 && n != null);
};

const KEY = [[ShieldCheck, "venues verified", "text-emerald-600"], [Flag, "flagged venues", "text-amber-600"], [Ban, "failed venues", "text-red-600"],
  [FileMinus, "light pages", "text-amber-600"], [Layers, "spill pages", "text-red-600"], [ImageOff, "missing images", "text-stone-500"],
  [FileQuestion, "pages not checked", "text-stone-400"], [Lock, "publish blockers", "text-red-600"]];

const IconKey = () => (
  <div data-testid="kit-icon-key" className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-[11px] text-stone-500">
    {KEY.map(([Icon, label, tone]) => <span key={label} className="inline-flex items-center gap-1"><Icon size={12} className={tone} />{label}</span>)}
  </div>
);

const Counts = ({ k }) => {
  const list = issues(k);
  return (
    <div data-testid={`kit-counts-${k.id}`} className="flex flex-wrap gap-x-2.5 gap-y-1 text-xs font-mono-ui">
      <Pill id={`kit-count-ok-${k.id}`} icon={ShieldCheck} n={k.counts.ok} tip={`${k.counts.ok} venue(s) web-verified`} tone="text-emerald-600" />
      {list.map(([key, icon, n, tip, tone]) => <Pill key={key} id={`kit-count-${key}-${k.id}`} icon={icon} n={n} tip={tip} tone={tone} />)}
      {!list.length && <Pill id={`kit-count-clear-${k.id}`} icon={CheckCircle2} n="" tip="No problems found" tone="text-emerald-600" />}
    </div>
  );
};

const KitRow = memo(({ k, reload, now }) => {
  const nav = useNavigate();
  const busy = isBusy(k);
  const slow = busy && k.step_at && ago(k.step_at, now) >= 600;
  return (
    <li data-testid={`kit-card-${k.id}`} onClick={() => nav(`/kits/${k.id}`)}
      className={`grid grid-cols-12 items-center gap-3 px-5 py-2.5 border-t border-stone-100 cursor-pointer transition-colors ${slow ? "bg-red-50/60 hover:bg-red-50" : "hover:bg-stone-50"}`}>
      <div className="col-span-3 min-w-0">
        <div className="font-display text-[#2B2A26] truncate">{k.destination}</div>
        <div className="text-[11px] text-stone-500 truncate">{sub(k)}</div>
        {k.bespoke?.name && <BespokeInfo k={k} reload={reload} />}
        <JobStopBadge k={k} />
      </div>
      <div className="col-span-4 min-w-0">{busy ? <Step k={k} /> : <StatusBadge status={kitStatus(k)} testId={`kit-status-${k.id}`} />}</div>
      <div className="col-span-2">{busy ? <JobInfo k={k} now={now} /> : <Counts k={k} />}</div>
      <div data-testid={`kit-price-${k.id}`} className="col-span-1 text-sm font-mono-ui text-stone-600">${k.product?.price}</div>
      <div className="col-span-2 flex justify-end">{busy ? <JobActions k={k} reload={reload} /> : <LiveToggle k={k} reload={reload} className="w-44" />}</div>
    </li>
  );
});
KitRow.displayName = "KitRow";

export default function Dashboard() {
  const [kits, setKits] = useState(null);
  const load = useCallback(() => http.get("/kits").then((r) => setKits(r.data)), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const [open, setOpenRaw] = useState(() => { try { return JSON.parse(localStorage.getItem("open-edition-groups") || "{}"); } catch { return {}; } });
  const setOpen = (f) => setOpenRaw((o) => { const n = typeof f === "function" ? f(o) : f; localStorage.setItem("open-edition-groups", JSON.stringify(n)); return n; });
  const [att, setAtt] = useState(null);
  useEffect(() => {
    const get = () => http.get("/ops/attention").then((r) => setAtt(r.data)).catch(() => {});
    get();
    const t = setInterval(get, 30000);
    return () => clearInterval(t);
  }, []);
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all";
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const all = (kits || []).filter((k) => !k.bespoke?.name);
  const needle = q.trim().toLowerCase();
  const list = needle ? all.filter((k) => `${k.destination} ${k.edition || ""} ${(k.subjects || []).join(" ")} ${k.bespoke?.name || ""}`.toLowerCase().includes(needle)) : all;
  const shown = applyFilter(list, filter, isBusy);
  return (
    <div className="p-8 lg:p-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10 rise">
        <div>
          <div className="eyebrow">Production Desk</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2A26] mt-2">Your Kits</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        <RebuildAll count={all.filter((k) => !k.bespoke && !["standalone", "guide"].includes(k.collection)).length} />
        <Link to="/new" data-testid="dashboard-new-kit-button" className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-white px-6 py-3 font-bold hover:bg-[#B8973A] transition-colors">
          <Plus size={18} /> Build a new kit
        </Link>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat id="stat-total" label="Total kits" value={all.length} />
        <Stat id="stat-review" label="Needs a look" value={applyFilter(all, "review", isBusy).length} />
        <Stat id="stat-generating" label="Running" value={all.filter(isBusy).length} />
        <Stat id="stat-published" label="Live · Ready" value={`${all.filter((k) => k.product?.published).length} · ${all.filter((k) => k.ready && !k.product?.published).length}`} />
      </div>
      {all.length > 0 && (
        <Fold id="g-attention" title="Needs attention" badge={att?.total}
          hint={att ? (att.total ? [att.deliveries && `${att.deliveries} delivery issue(s)`, att.blockers && `${att.blockers} blocked kit(s)`, att.prices && `${att.prices} unverified price(s)`].filter(Boolean).join(" · ") : "all clear") : "deliveries and publish blockers"} big>
          <DeliveryStatus />
          <PublishBlockers />
        </Fold>
      )}
      {all.length > 0 && (
        <Fold id="g-automation" title="Automation" hint="Autopilot and whole-library checks" big>
          <Autopilot />
          <CheckAllPanel />
        </Fold>
      )}
      <Fold id="g-content" title="Content tools" hint="fill, repair and tidy kit content" big closed>
        <Fold id="fill-days" title="Fill thin days"><FillDays /></Fold>
        <Fold id="repairs" title="Repairs"><RepairsPanel /></Fold>
        <Fold id="std-info" title="Information boxes"><StdInfo /></Fold>
        <Fold id="prices" title="Prices"><PriceStrip /></Fold>
        <Fold id="light-fill" title="Fill light pages"><LightFill /></Fold>
      </Fold>
      <Fold id="g-look" title="Look and style" hint="house style guide and PDF looks" big closed>
        <Fold id="style-guide" title="House style guide"><StyleGuidePicker /></Fold>
        <Fold id="kit-look" title="Kit look"><LookPanel /></Fold>
        <Fold id="guides-look" title="Guides look"><OtherLookPanel /></Fold>
      </Fold>
      {all.length > 0 && (
        <Fold id="g-costs" title="AI costs" hint="average cost per kit by tier and edition" big closed>
          <CostByTier />
        </Fold>
      )}
      <Fold id="g-kits" title="All kits" hint={`${all.length} kit(s)`} big>
      {kits && all.length === 0 && (
        <div data-testid="dashboard-empty" className="rounded-xl border border-dashed border-[#C9A847]/30 p-16 text-center">
          <div className="font-display text-2xl text-[#2B2A26]">No kits yet</div>
          <p className="text-stone-500 mt-2">Start with an edition, a destination and a season.</p>
        </div>
      )}
      {all.length > 0 && (
        <div className="relative max-w-md mb-4">
          <Search size={16} className="absolute left-3 top-[18px] -translate-y-1/2 text-stone-400" />
          <input data-testid="kit-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search destinations…"
            onKeyDown={(e) => { if (e.key === "Enter" && shown.length === 1) nav(`/kits/${shown[0].id}`); if (e.key === "Escape") setQ(""); }}
            className="w-full rounded-full border border-stone-300 bg-white pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-[#C9A847] focus:ring-2 focus:ring-[#C9A847]/20 transition-colors" />
          {q && <button data-testid="kit-search-clear" onClick={() => setQ("")} className="absolute right-3 top-[18px] -translate-y-1/2 text-stone-400 hover:text-stone-700"><X size={15} /></button>}
          {needle && <div data-testid="kit-search-count" className="text-xs text-stone-500 mt-1 ml-3">{list.length} match{list.length === 1 ? "" : "es"}{shown.length === 1 ? " — press Enter to open" : ""}</div>}
        </div>
      )}
      {all.length > 0 && <KitFilters list={list} value={filter} busy={isBusy} onChange={(f) => setParams(f === "all" ? {} : { filter: f }, { replace: true })} />}
      {all.length > 0 && <IconKey />}
      {kits && all.length > 0 && !shown.length && <p data-testid="kit-filter-empty" className="rounded-xl border border-stone-200 bg-white px-5 py-8 text-sm text-stone-500">No kits match this filter.</p>}
      {groupByEdition(shown, (a, b) => price(a) - price(b)).map((g) => (
        <EditionGroup key={g.id} id={g.id} kits={g.kits} open={open[g.id] !== false} onToggle={() => setOpen((o) => ({ ...o, [g.id]: o[g.id] === false }))}
          title={g.title} color={g.color} render={(k) => <KitRow key={k.id} k={k} reload={load} now={isBusy(k) ? now : 0} />} />
      ))}
      </Fold>
    </div>
  );
}
